import { Download, RefreshCw, WifiOff, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';

import { useI18n } from '../i18n/useI18n';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const UPDATE_INTERVAL_MS = 60 * 60 * 1_000;

export function PwaExperience() {
  const { t } = useI18n();
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [registrationError, setRegistrationError] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent>();
  const updateServiceWorker = useRef<((reloadPage?: boolean) => Promise<void>) | undefined>(
    undefined,
  );

  useEffect(() => {
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
    const ios = /iPad|iPhone|iPod/u.test(navigator.userAgent);
    document.documentElement.classList.toggle('pwa-standalone', standalone);
    document.documentElement.classList.toggle('pwa-ios', standalone && ios);

    const online = () => setIsOnline(true);
    const offline = () => setIsOnline(false);
    const beforeInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    window.addEventListener('beforeinstallprompt', beforeInstall);

    let interval: ReturnType<typeof setInterval> | undefined;
    updateServiceWorker.current = registerSW({
      immediate: true,
      onNeedRefresh: () => setNeedRefresh(true),
      onOfflineReady: () => setOfflineReady(true),
      onRegisterError: () => setRegistrationError(true),
      onRegisteredSW: (_url, registration) => {
        if (!registration) return;
        interval = setInterval(() => void registration.update(), UPDATE_INTERVAL_MS);
      },
    });

    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
      window.removeEventListener('beforeinstallprompt', beforeInstall);
      if (interval) clearInterval(interval);
    };
  }, []);

  const install = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(undefined);
  };

  return (
    <aside aria-label={t('pwa.status')} aria-live="polite" className="pwa-status-stack">
      {!isOnline ? (
        <div className="pwa-status is-offline" role="status">
          <WifiOff aria-hidden="true" />
          <span>{t('pwa.offline')}</span>
        </div>
      ) : null}
      {needRefresh ? (
        <div className="pwa-status" role="status">
          <RefreshCw aria-hidden="true" />
          <span>{t('pwa.newVersion')}</span>
          <button
            className="button button-primary button-small"
            onClick={() => void updateServiceWorker.current?.(true)}
            type="button"
          >
            {t('pwa.update')}
          </button>
        </div>
      ) : null}
      {offlineReady ? (
        <DismissibleStatus closeLabel={t('pwa.close')} onDismiss={() => setOfflineReady(false)}>
          {t('pwa.offlineReady')}
        </DismissibleStatus>
      ) : null}
      {registrationError ? (
        <DismissibleStatus
          closeLabel={t('pwa.close')}
          isError
          onDismiss={() => setRegistrationError(false)}
        >
          {t('pwa.registrationError')}
        </DismissibleStatus>
      ) : null}
      {installPrompt ? (
        <div className="pwa-status" role="status">
          <Download aria-hidden="true" />
          <span>{t('pwa.installMessage')}</span>
          <button className="button button-primary button-small" onClick={install} type="button">
            {t('pwa.install')}
          </button>
          <button
            aria-label={t('pwa.hideInstall')}
            className="pwa-dismiss"
            onClick={() => setInstallPrompt(undefined)}
            type="button"
          >
            <X aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </aside>
  );
}

function DismissibleStatus({
  children,
  closeLabel,
  isError = false,
  onDismiss,
}: {
  readonly children: string;
  readonly closeLabel: string;
  readonly isError?: boolean;
  readonly onDismiss: () => void;
}) {
  return (
    <div className={isError ? 'pwa-status is-error' : 'pwa-status'} role="status">
      <span>{children}</span>
      <button aria-label={closeLabel} className="pwa-dismiss" onClick={onDismiss} type="button">
        <X aria-hidden="true" />
      </button>
    </div>
  );
}
